import { Link, NavLink, useParams } from 'react-router-dom';
import DocBody from '../components/DocBody';
import { getDoc, getDocsForPlatform, type DocMeta } from '../content/docs';

/**
 * Renders a single design-docs section (Architecture, Salesforce or Azure) as
 * its own top-level area. Content is published to the platform-content blob
 * from the synergy-design-docs repo; every .md under the section's docs/ folder
 * appears here automatically. The sidebar mirrors the repo's folder structure
 * as a nested, collapsible tree (native <details>), auto-expanded along the
 * path to the current page.
 */
function folderLabel(name: string): string {
  return name.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

interface TreeNode {
  files: DocMeta[];
  folders: Map<string, TreeNode>;
}

const emptyNode = (): TreeNode => ({ files: [], folders: new Map() });
const byTitle = (a: DocMeta, b: DocMeta) => a.title.localeCompare(b.title);

export default function PlatformDocs({ platformId, title }: { platformId: string; title: string }) {
  const params = useParams();
  const rest = (params['*'] ?? '').replace(/\/+$/, '');
  const sectionDocs = getDocsForPlatform(platformId);

  // Default landing = the section's overview page, else the first doc.
  const defaultSlug =
    sectionDocs.find((d) => d.slug === `${platformId}/overview`)?.slug ?? sectionDocs[0]?.slug;
  const slug = rest ? `${platformId}/${rest}` : defaultSlug;
  const doc = slug ? getDoc(slug) : undefined;
  const activeRel = slug ? slug.slice(platformId.length + 1) : '';

  // Build a nested folder tree from each doc's path within the section. The
  // leading path segments are folders; the last segment is the page.
  const root = emptyNode();
  for (const d of sectionDocs) {
    const parts = d.slug.slice(platformId.length + 1).split('/');
    parts.pop();
    let node = root;
    for (const p of parts) {
      if (!node.folders.has(p)) node.folders.set(p, emptyNode());
      node = node.folders.get(p)!;
    }
    node.files.push(d);
  }

  const renderFile = (d: DocMeta) => {
    const rel = d.slug.slice(platformId.length + 1);
    return (
      <li key={d.slug}>
        <NavLink
          to={`/${platformId}/${rel}`}
          className={({ isActive }) => (isActive || slug === d.slug ? 'active' : undefined)}
        >
          {d.title}
        </NavLink>
      </li>
    );
  };

  // Recursively render a tree node: files at this level, then collapsible
  // sub-folders. A folder is open if the active page lives inside it.
  const renderNode = (node: TreeNode, pathPrefix: string) => {
    const folderNames = [...node.folders.keys()].sort();
    return (
      <>
        {node.files.length > 0 && <ul>{[...node.files].sort(byTitle).map(renderFile)}</ul>}
        {folderNames.map((name) => {
          const childPath = pathPrefix ? `${pathPrefix}/${name}` : name;
          const open = activeRel === childPath || activeRel.startsWith(`${childPath}/`);
          return (
            <details key={name} className="docs-tree-folder" open={open}>
              <summary>{folderLabel(name)}</summary>
              <div className="docs-tree-children">{renderNode(node.folders.get(name)!, childPath)}</div>
            </details>
          );
        })}
      </>
    );
  };

  return (
    <div className="docs-layout">
      <aside className="docs-sidebar" aria-label={`${title} navigation`}>
        <nav>
          <div className="docs-side-label">{title}</div>
          {sectionDocs.length === 0 ? (
            <p className="note">No documents published yet.</p>
          ) : (
            renderNode(root, '')
          )}
        </nav>
      </aside>
      <article className="doc-content">
        <nav className="breadcrumb" aria-label="Breadcrumb">
          <Link to={`/${platformId}`}>{title}</Link>
          {doc && (
            <>
              <span aria-hidden="true"> / </span>
              <span>{doc.title}</span>
            </>
          )}
        </nav>
        {doc ? (
          <DocBody doc={doc} />
        ) : (
          <>
            <h2>{title}</h2>
            <p className="note">
              No documentation has been published yet. Authors add markdown under{' '}
              <code>{platformId}/docs/</code> in the <code>synergy-design-docs</code> repo;
              it appears here on the next site build.
            </p>
          </>
        )}
      </article>
    </div>
  );
}
